import { subscribeToBackInStock } from './back-in-stock-signup.js';

// =====================================================================
// Custom popup component
// =====================================================================
class CustomPopup extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll("[data-popup-close]").forEach((closeTrigger) => {
      closeTrigger.addEventListener("click", this.hide.bind(this, false));
    });
    this.addEventListener("keyup", (event) => {
      if (event.code?.toUpperCase() === "ESCAPE") this.hide();
    });

    // this.addEventListener('click', (event) => {
    //     if (event.target === this) this.hide();
    // });
  }

  show() {
    document.body.classList.add("overflow-hidden");
    this.classList.add("visible");
  }

  hide() {
    document.body.classList.remove("overflow-hidden");
    this.classList.remove("visible");
  }
}
customElements.define("custom-popup", CustomPopup);

// =====================================================================
// Custom popup opener — triggers a popup by click
// =====================================================================
class CustomPopupOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");
    if (!button) return;

    button.addEventListener("click", () => {
      const popup = document.querySelector(this.getAttribute("data-popup"));
      if (popup) popup.show();
    });
  }
}
customElements.define("custom-popup-opener", CustomPopupOpener);

// =====================================================================
// Tooltip component
// =====================================================================
class ToolTip extends HTMLElement {
  constructor() {
    super();
    this.el = this;
    this.inner = this.querySelector("[data-tool-tip-inner]");
    this.closeButton = this.querySelector("[data-tool-tip-close]");
    this.toolTipContent = this.querySelector("[data-tool-tip-content]");
    this.toolTipTitle = this.querySelector("[data-tool-tip-title]");

    this.triggers = document.querySelectorAll("[data-tool-tip-trigger]");

    document.addEventListener("tooltip:open", (e) => {
      this._open(e.detail.context, e.detail.content);
    });
  }

  _open(context, insertedHtml) {
    this.toolTipContent.innerHTML = insertedHtml;

    // Only show a title for product availability tooltips
    if (context != "store-availability") {
      this.toolTipTitle.remove();
    }

    if (this.closeButton) {
      this.closeButton.addEventListener("click", () => {
        this._close();
      });
    }

    document.documentElement.addEventListener("click", () => {
      this._close();
    });

    document.documentElement.addEventListener("keydown", (event) => {
      if (event.code === "Escape") this._close();
    });

    this.el.dataset.toolTipOpen = true;
    this.el.dataset.toolTip = context;
  }

  _close() {
    this.toolTipContent.innerHTML = "";
    this.el.dataset.toolTipOpen = "false";
    this.el.dataset.toolTip = "";

    this.closeButton.removeEventListener("click");
    document.documentElement.removeEventListener("click");
    document.documentElement.removeEventListener("keydown");
  }
}

customElements.define("tool-tip", ToolTip);

// =====================================================================
// Tooltip trigger — opens a tooltip via custom event
// =====================================================================
class ToolTipTrigger extends HTMLElement {
  constructor() {
    super();
    this.el = this;
    this.toolTipContent = this.querySelector("[data-tool-tip-content]");
    this.init();
  }

  init() {
    const toolTipOpen = new CustomEvent("tooltip:open", {
      detail: {
        context: this.dataset.toolTip,
        content: this.toolTipContent.innerHTML,
      },
      bubbles: true,
    });

    this.el.addEventListener("click", (e) => {
      e.stopPropagation();
      this.dispatchEvent(toolTipOpen);
    });
  }
}
customElements.define("tool-tip-trigger", ToolTipTrigger);

// =====================================================================
// A/B test: variant 1 — sticky ATC button when scrolled out of view
// (only fires when the .test-1-variant-1 class is present on the page)
// =====================================================================
if (document.querySelector('.test-1-variant-1') && window.location.pathname.includes('/products/')) {
  const handleATCSticky = (e) => {
    const atcButton = document.querySelector('.test-1-variant-1 form[id^=product-form-] .add-to-cart-container button[type=submit]');
    const atcButtonContainer = document.querySelector('.test-1-variant-1 form[id^=product-form-] .add-to-cart-container');
    if (atcButtonContainer.getBoundingClientRect().bottom < 0) {
      // ATC scrolled out of view: pin it to the bottom of the viewport
      atcButtonContainer.classList.add('sticky');
    } else {
      atcButtonContainer.classList.remove('sticky');
    }
  };

  document.addEventListener('scroll', handleATCSticky);
}

// =====================================================================
// A/B test: variant 5 — homepage horizontal collection scroll
// Adds a "reachedEnd" class when user has scrolled to the end of the
// horizontal grid (used to fade out the right-side scroll hint icon)
// =====================================================================
function initHorizontalScrollCheck() {
  if (!document.querySelector('.test-5-variant-1')) return;

  const container = document.querySelector('.homepage-visual-category');
  const grid = container?.querySelector('.grid');

  if (!container || !grid) return;

  grid.addEventListener('scroll', () => {
    const isAtEnd = Math.ceil(grid.scrollLeft) + grid.clientWidth >= grid.scrollWidth - 50;

    if (isAtEnd) {
      container.classList.add('reachedEnd');
    } else {
      container.classList.remove('reachedEnd');
    }
  });
}
initHorizontalScrollCheck();

// =====================================================================
// A/B test: variant 2 — PDP layout overhaul
// Re-arranges the product page layout (vertical thumbnails, merged
// price+rating, hero video relocation, etc.) when the .test-2-variant
// class is present.
// =====================================================================
class PDPTestVariant {
  constructor() {
    this.isVariant = document.querySelector('.test-2-variant');
    if (!this.isVariant) return;

    this.init();
  }

  init() {
    this.mergePriceAndReviews();
    this.injectSentimentText();
    this.moveSizeGuideLink();
    this.moveVendorText();
    this.moveBenefits();
    this.hideEmptyAppDivs();
    this.moveHeroVideo();
    this.injectVerticalThumbnails();
  }

  // Wraps the price block + ratings widget in a single flex container
  mergePriceAndReviews() {
    const priceBlock = document.querySelector('.product-price-block');
    const ratingsWidget = document.querySelector('.klaviyo-star-rating-widget')?.closest('.mt-8');

    if (priceBlock && ratingsWidget) {
      const combinedContainer = document.createElement('div');
      combinedContainer.classList.add('combined-price-rating-container');

      priceBlock.parentNode.insertBefore(combinedContainer, priceBlock);
      combinedContainer.appendChild(ratingsWidget);
      combinedContainer.appendChild(priceBlock);
    }
  }

  // Pulls the hero video's text content into a sentiment message under the title
  injectSentimentText() {
    const titleBlock = document.querySelector('.product-title-block');
    // Only inject if there's actual sentiment text in the hero video
    const originalSentimentText = document.querySelector('.pdp-hero-video');

    if (titleBlock && originalSentimentText && originalSentimentText.innerText != '') {
      const sentimentText = document.createElement('div');
      sentimentText.classList.add('product-sentiment-message');
      sentimentText.innerText = originalSentimentText.innerText.replace(/\s+/g, ' ').trim();
      titleBlock.after(sentimentText);
    }
  }

  // Moves the size guide block inside the variant picker
  moveSizeGuideLink() {
    const sizeGuide = document.querySelector('.size-guide');
    const variantPicker = document.querySelector('.product-variant-picker-block');

    if (sizeGuide && variantPicker) {
      variantPicker.appendChild(sizeGuide);
    }
  }

  // Moves the vendor text below the price/rating combined container
  moveVendorText() {
    const vendorBlock = document.querySelector('.vendorText');
    const combinedPrice = document.querySelector('.combined-price-rating-container');

    if (vendorBlock && combinedPrice) {
      combinedPrice.after(vendorBlock);
    }
  }

  // Moves the benefits icon list to appear right after the ATC section
  moveBenefits() {
    const benefits = document.querySelector('.benefits');
    const atcSection = document.querySelector('[x-data="ProductForm"]');

    if (benefits && atcSection) {
      atcSection.after(benefits);
    }
  }

  // Hides empty app blocks (Simple Bundles, Globo Product Groups) so
  // they don't leave empty .mt-8 wrappers on the page
  hideEmptyAppDivs() {
    const appDivsToCheck = document.querySelectorAll('#simple-bundles-io-options, .globo-product-groups-detail');
    appDivsToCheck.forEach((div) => {
      if (div.children.length === 0) {
        div.closest('.mt-8').style.display = 'none';
      }
    });
  }

  // Maps each product template to a specific hero video URL and
  // moves the hero video block to the new location
  moveHeroVideo() {
    const videosAndTemplateMap = {
      "douspartgold": "https://cdn.shopify.com/videos/c/o/v/ed1059af3a584f1db6b5615f2f03eabd.mp4",
      "douspartsilver": "https://cdn.shopify.com/videos/c/o/v/5fee6f92ae3b4652acae2433401d2593.mp4",
      "mementogold": "https://cdn.shopify.com/videos/c/o/v/8611bacdc66147d0b41085b3fde81860.mp4",
      "memento": "https://cdn.shopify.com/videos/c/o/v/371b55a5bb804a1e8f489cfc838f6fa9.mp4",
      "sacredheartgold": "https://cdn.shopify.com/videos/c/o/v/d9ea3469c4fe4d0abcedfcf146790320.mp4",
      "sacredheart": "https://cdn.shopify.com/videos/c/o/v/6ba78aa5550044beb2fe10192abfb8b7.mp4",
      "tilldeathdiamond": "https://cdn.shopify.com/videos/c/o/v/230fa706f02a4bd8a401f1ff3552c21a.mp4",
      "tilldeathsolidgold": "https://cdn.shopify.com/videos/c/o/v/8a5b7c2310d7402f99c0da7c4a8ec0ec.mp4",
      "tilldeathgold": "https://cdn.shopify.com/videos/c/o/v/5e9b9f1c7a714b1bb9d1e13a0fef7a55.mp4",
      "breakable-goldsilver": "https://cdn.shopify.com/videos/c/o/v/62ea000ac8d043da8c6f54c6aea7b130.mp4",
      "breakable-heart": "https://cdn.shopify.com/videos/c/o/v/d71832b546d94fb8bbf36a4d059d2e4a.mp4",
      "tilldeath": "https://cdn.shopify.com/videos/c/o/v/5e9b9f1c7a714b1bb9d1e13a0fef7a55.mp4"
    };

    const template = document.querySelector('body').getAttribute('data-template');
    const newVideoURL = videosAndTemplateMap[template];

    const heroVideo = document.querySelector('.pdp-hero-video');
    const newHeroLocation = document.querySelector('.pdp-hero-new-place');
    if (heroVideo && newHeroLocation) {
      if (newVideoURL) heroVideo.querySelector('video source').src = newVideoURL;
      newHeroLocation.after(heroVideo);
    }
  }

  // Builds a vertical thumbnail rail beside the main media slideshow
  injectVerticalThumbnails() {
    const mediaContainer = document.querySelector('.product-media-container');

    // Collect all slides — each <li> contains an <img> we can clone
    let slides = mediaContainer
      ? [...mediaContainer.querySelectorAll('.splide__slide')]
      : [];

    if (!mediaContainer || slides.length < 2) return;

    // Build the thumb rail
    const rail = document.createElement('div');
    rail.classList.add('pdp-thumb-rail');

    slides.forEach((slide, i) => {
      if (i === 1 && document.querySelector('.test-2-variant-2')) {
        const heroVideo = document.querySelector('.pdp-hero-video video');
        if (heroVideo) {
          const videoThumb = document.createElement('button');
          videoThumb.classList.add('pdp-thumb', 'video-thumb');
          videoThumb.setAttribute('aria-label', 'View video');

          const vImg = document.createElement('img');
          // Use the video's poster if available, otherwise a fallback
          vImg.src = heroVideo.poster || '';
          vImg.width = 80;
          vImg.height = 80;
          vImg.style.objectFit = 'cover';

          videoThumb.appendChild(vImg);
          rail.appendChild(videoThumb);

          videoThumb.addEventListener('click', () => {
            const movedVideo = document.querySelector('.moved-video');
            movedVideo?.closest('.splide__slide')?.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest'
            });
          });
        }
      }

      const sourceImg = slide.querySelector('img');
      if (!sourceImg) return;

      const thumb = document.createElement('button');
      thumb.classList.add('pdp-thumb');
      if (i == 0) thumb.classList.add('pdp-thumb--active');
      thumb.setAttribute('aria-label', `View image ${i + 1}`);
      thumb.dataset.thumbIndex = i;

      const img = document.createElement('img');
      img.src = sourceImg.src;
      img.alt = sourceImg.alt || '';
      img.loading = 'lazy';
      img.width = 80;
      img.height = 80;

      // Use a higher-resolution src from srcset when available
      if (sourceImg.srcset) {
        // Pick the smallest entry from srcset for the thumb
        const firstSrcset = sourceImg.srcset.split(',')[0].trim().split(' ')[0];
        if (firstSrcset) img.src = firstSrcset;
      }

      thumb.appendChild(img);
      rail.appendChild(thumb);

      // Click on a thumb scrolls the corresponding main image into view
      thumb.addEventListener('click', () => {
        slide.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    });

    // Insert the rail BEFORE the splide so it sits to the left
    const splide = mediaContainer.querySelector('.splide--product');
    if (splide) {
      // Wrap splide + rail together so they can flex side by side
      const wrapper = document.createElement('div');
      wrapper.classList.add('pdp-media-with-thumbs');

      if (document.querySelector('.test-2-variant-2')) {
        const heroVideo = document.querySelector('.pdp-hero-video video');
        if (heroVideo) {
          const cloneVideo = heroVideo.cloneNode(true);
          cloneVideo.classList.add('moved-video');
          const clonedHeroVideoWrapper = document.createElement('li');
          clonedHeroVideoWrapper.classList.add('splide__slide', 'bg-scheme-background', 'max-lg:[.splide:not(.is-active)_&]:w-full');
          clonedHeroVideoWrapper.appendChild(cloneVideo);
          splide.querySelector('.splide__list').insertBefore(clonedHeroVideoWrapper, splide.querySelectorAll('.splide__list li.splide__slide')[1]);
        }
      }

      splide.parentNode.insertBefore(wrapper, splide);
      wrapper.appendChild(splide);
      wrapper.appendChild(rail);
    }

    slides = mediaContainer
      ? [...mediaContainer.querySelectorAll('.splide__slide')]
      : [];
    // Set up the scroll observer to track which thumb is active
    this._setupThumbObserver(slides, rail);
  }

  // Watches scroll position and marks the closest slide's thumb as active
  _setupThumbObserver(slides, rail) {
    const thumbButtons = [...rail.querySelectorAll('.pdp-thumb')];

    let activeIndex = 0;
    let rafId = null;

    const setActive = (index) => {
      if (index === activeIndex) return;
      activeIndex = index;
      thumbButtons.forEach((btn, i) => {
        btn.classList.toggle('pdp-thumb--active', i === index);
      });
    };

    // Pick whichever slide's top edge is closest to the viewport midpoint.
    // One deterministic winner, zero flicker.
    const pick = () => {
      const mid = window.innerHeight / 2;
      let bestIdx = 0;
      let bestDist = Infinity;

      slides.forEach((slide, i) => {
        const { top } = slide.getBoundingClientRect();
        // Distance from the slide's top edge to the viewport midpoint
        const dist = Math.abs(top - mid);
        if (dist < bestDist) {
          bestDist = dist;
          bestIdx = i;
        }
      });

      setActive(bestIdx);
      rafId = null;
    };

    const onScroll = () => {
      if (!rafId) rafId = requestAnimationFrame(pick);
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    // Run once on init so the first thumb starts as active
    pick();
  }
}

new PDPTestVariant();

// =====================================================================
// Homepage slideshow — resume autoplay after a manual interaction
// (clicking a pagination dot or arrow). Without this, Splide pauses
// autoplay as soon as the user interacts.
// =====================================================================
(function () {
  function attachAutoplayResume(splideEl) {
    if (!splideEl) return;
    if (splideEl.dataset.autoplayResumeAttached === 'true') return;
    splideEl.dataset.autoplayResumeAttached = 'true';

    splideEl.addEventListener('click', function (e) {
      const trigger = e.target.closest('.splide__pagination__page, .splide__arrow');
      if (!trigger) return;

      setTimeout(function () {
        try {
          const alpineData = window.Alpine && window.Alpine.$data(splideEl);
          if (
            alpineData &&
            alpineData.autoplay &&
            alpineData.splide &&
            alpineData.splide.Components &&
            alpineData.splide.Components.Autoplay
          ) {
            alpineData.splide.Components.Autoplay.play();
          }
        } catch (err) {
          // silently ignore
        }
      }, 150);
    });
  }

  function setupAll() {
    document.querySelectorAll('.splide').forEach(function (splideEl) {
      const xData = splideEl.getAttribute('x-data') || '';
      if (!xData.includes('Slideshow(')) return;
      attachAutoplayResume(splideEl);
    });
  }

  function init() {
    setupAll();
    setTimeout(setupAll, 1000);
    setTimeout(setupAll, 3000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', setupAll);
})();

// =====================================================================
// Homepage slideshow — enable swipe/drag on the FIRST slideshow even
// when autoplay is on. The theme disables drag by default whenever
// autoplay is enabled, so we re-enable it for the first one only.
// =====================================================================
(function () {
  function enableDragOnFirstSlideshow() {
    const splideEls = document.querySelectorAll('.splide');
    let first = null;
    for (let i = 0; i < splideEls.length; i++) {
      const xData = splideEls[i].getAttribute('x-data') || '';
      if (xData.includes('Slideshow(')) { first = splideEls[i]; break; }
    }
    if (!first) return false;
    if (first.dataset.dragEnabledByCustom === 'true') return true;

    try {
      const data = window.Alpine && window.Alpine.$data(first);
      if (data && data.splide && data.splide.Components && data.splide.Components.Drag) {
        data.splide.Components.Drag.disable(false);
        first.dataset.dragEnabledByCustom = 'true';

        // Bonus: after a swipe, resume autoplay (same as we do for clicks)
        if (data.autoplay && data.splide.Components.Autoplay) {
          data.splide.on('dragged', function () {
            data.splide.Components.Autoplay.play();
          });
        }
        return true;
      }
    } catch (err) { /* silent */ }
    return false;
  }

  function init() {
    if (enableDragOnFirstSlideshow()) return;
    setTimeout(enableDragOnFirstSlideshow, 500);
    setTimeout(enableDragOnFirstSlideshow, 1500);
    setTimeout(enableDragOnFirstSlideshow, 3000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();

// =====================================================================
// PDP — Single product variant picker setup
// Moves the size guide block next to the size legend so it appears
// inline ("Size:  📏 Size Guide" instead of as a separate block below).
// =====================================================================
(function () {
  function setupVariantPicker() {
    const variantPicker = document.querySelector('.product-variant-picker-block');
    if (!variantPicker) return;
    if (variantPicker.dataset.compactSetup === 'true') return;
    variantPicker.dataset.compactSetup = 'true';

    const legend = variantPicker.querySelector('legend');
    if (!legend) return;

    const sizeGuide = document.querySelector('.size-guide');
    if (sizeGuide && !variantPicker.contains(sizeGuide)) {
      let originalWrapper = sizeGuide.closest('.mt-8');
      if (originalWrapper && variantPicker.contains(originalWrapper)) originalWrapper = null;

      const headingRow = document.createElement('div');
      headingRow.className = 'pdp-size-heading-row';
      legend.parentNode.insertBefore(headingRow, legend);
      headingRow.appendChild(legend);
      sizeGuide.classList.add('pdp-size-guide-inline');
      headingRow.appendChild(sizeGuide);

      if (originalWrapper && originalWrapper.textContent.trim() === '') {
        originalWrapper.style.display = 'none';
      }
    }
  }

  function init() {
    setupVariantPicker();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setTimeout(init, 150));
  } else {
    setTimeout(init, 150);
  }
})();

// =====================================================================
// Infinite scroll for collection pages
// + Scroll position / anchor memory (CLP/PLP -> PDP -> back)
//
// Root cause of the scroll-reset bug: this block sets
// `history.scrollRestoration = 'manual'` (required for infinite scroll
// to behave predictably), but never implemented the manual restore
// that setting requires — so on back navigation the browser did
// nothing and landed at the top. Fixed below.
//
// v2: only restores scroll on an actual back/forward navigation
// (checked via the Navigation Timing API), never on a plain refresh;
// catch-up page fetches run in parallel instead of one-by-one for a
// faster restore; shows "Restoring your place..." while it catches up.
// =====================================================================
(function () {
  // `history.scrollRestoration = 'manual'` is set further down, ONLY when
  // this page actually has a native infinite-scroll grid with more pages
  // to fetch (see initInfiniteScroll() below) — that's the one case where
  // we truly need to take over restoration ourselves.
  //
  // v3: this used to be set here, unconditionally, for every single page
  // on the site. That's what actually broke the scroll-reset bug for most
  // shoppers: this store's real collection and search results are
  // rendered by the Searchspring app (a `.ss__results-grid`, not this
  // theme's own `ul.collection-list` / `ul.grid` markup that findGrid()
  // below looks for), so on those pages nothing here ever ran a manual
  // restore — but the browser's own automatic restoration had already
  // been disabled for them regardless, so back navigation landed at the
  // top with nothing to put it anywhere else. Scoping the manual mode to
  // only the pages this file actually manages leaves every other page
  // (Searchspring-rendered collection/search results included) on the
  // browser's normal automatic scroll restoration, which needs no help
  // from us to begin with.

  // --------------------------- SCROLL MEMORY ---------------------------
  function lcScrollMemoryKey() {
    return 'lc-collection-scroll:' + location.pathname + location.search;
  }

  function lcSaveScrollMemory(grid) {
    if (!grid) grid = findGrid(document);
    if (!grid) return;
    try {
      sessionStorage.setItem(
        lcScrollMemoryKey(),
        JSON.stringify({
          scrollY: window.scrollY,
          itemCount: grid.children.length,
          savedAt: Date.now(),
        })
      );
    } catch (e) {
      /* sessionStorage disabled/full — fail silently, no scroll memory this time */
    }
  }

  function lcReadScrollMemory() {
    try {
      var raw = sessionStorage.getItem(lcScrollMemoryKey());
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function lcClearScrollMemory() {
    try {
      sessionStorage.removeItem(lcScrollMemoryKey());
    } catch (e) {}
  }

  function lcBindProductClickSave(grid) {
    if (!grid || grid.dataset.lcScrollMemoryBound === 'true') return;
    grid.dataset.lcScrollMemoryBound = 'true';
    grid.addEventListener('click', function (e) {
      var link = e.target.closest('a[href*="/products/"]');
      if (link) lcSaveScrollMemory(grid);
    });
  }

  // True only when the browser's back/forward buttons (or an
  // equivalent in-site "go back" action) triggered this load — never
  // for a plain refresh or a fresh link click. That's what keeps a
  // manual F5 from unexpectedly restoring an old scroll position.
  function lcIsBackForwardNavigation() {
    try {
      var entries = performance.getEntriesByType('navigation');
      if (entries && entries.length) return entries[0].type === 'back_forward';
      if (performance.navigation) return performance.navigation.type === 2; // legacy Safari
    } catch (e) {}
    return false;
  }

  // Backup save — covers cases that skip the click handler (e.g. a
  // product opened via keyboard Enter that still bubbles a click will
  // already be caught above; this is for anything else that navigates
  // away, so "back" still has something recent to restore from).
  window.addEventListener('pagehide', function () {
    lcSaveScrollMemory();
  });

  // Restores scroll on a back/forward-cache restore. In this case the
  // DOM (including any extra infinite-scroll products) is exactly as
  // the user left it, so no rehydration is needed — just reposition.
  // event.persisted is only ever true for a real bfcache restore, so
  // this never fires on a plain refresh.
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return; // fresh loads are handled in initInfiniteScroll()
    var saved = lcReadScrollMemory();
    if (!saved) return;
    window.scrollTo(0, saved.scrollY);
  });
  // ------------------------- END SCROLL MEMORY -------------------------

  function findNextLink(root) {
    const links = root.querySelectorAll('a[href*="page="]');
    return Array.from(links).find(function (a) {
      const txt = (a.textContent || '').trim();
      const aria = a.getAttribute('aria-label') || '';
      return /^next$/i.test(txt) || /next/i.test(aria);
    });
  }

  function findGrid(root) {
    return (
      root.querySelector('[data-fragment-id="results"] ul.collection-list') ||
      root.querySelector('[data-fragment-id="results"] ul') ||
      root.querySelector('ul.collection-list') ||
      root.querySelector('ul.grid')
    );
  }

  function isPlaceholder(el) {
    if (!el || el.nodeType !== 1) return true;
    if (el.getAttribute('aria-hidden') === 'true') return true;
    if (el.getAttribute('role') === 'presentation') return true;
    const cls = el.className || '';
    if (/placeholder/i.test(cls)) return true;
    if (el.children.length === 0 && !el.textContent.trim()) return true;
    return false;
  }

  function removePlaceholders(grid) {
    Array.from(grid.children).forEach(function (child) {
      if (isPlaceholder(child)) child.remove();
    });
  }

  // Shared per-product hydration (images, Alpine init, etc.) — used by
  // both the normal sequential loadMore() and the parallel catch-up path.
  function hydrateNewProduct(product) {
    product.querySelectorAll('img').forEach(function (img) {
      img.loading = 'eager';
      if (img.dataset.src && (!img.getAttribute('src') || img.src.startsWith('data:'))) {
        img.src = img.dataset.src;
      }
      if (img.dataset.srcset && !img.getAttribute('srcset')) {
        img.srcset = img.dataset.srcset;
      }
    });
    product.querySelectorAll('image-with-placeholder').forEach(function (el) {
      el.classList.add('is-loaded', 'loaded', 'is-active');
    });
    product.querySelectorAll('[x-cloak]').forEach(function (el) {
      el.removeAttribute('x-cloak');
    });
    if (window.Alpine && typeof window.Alpine.initTree === 'function') {
      try { window.Alpine.initTree(product); } catch (e) {}
    }
  }

  function appendProductsFromDoc(productGrid, doc) {
    const newGrid = findGrid(doc);
    if (!newGrid) return;

    removePlaceholders(productGrid);

    const realProductsHtml = Array.from(newGrid.children)
      .filter(function (child) { return !isPlaceholder(child); })
      .map(function (child)   { return child.outerHTML; })
      .join('');

    const startIndex = productGrid.children.length;
    productGrid.insertAdjacentHTML('beforeend', realProductsHtml);

    Array.from(productGrid.children).slice(startIndex).forEach(hydrateNewProduct);
  }

  // Builds page N's URL by swapping the "page" query param on a known
  // pagination link (e.g. the scraped "next" href), preserving every
  // other param (filters, sort, etc.).
  function buildPageUrl(sampleUrl, pageNum) {
    try {
      const url = new URL(sampleUrl, window.location.origin);
      url.searchParams.set('page', pageNum);
      return url.toString();
    } catch (e) {
      return sampleUrl;
    }
  }

  // ---- State tracked across re-inits so we can clean up properly ----
  var activeObserver = null;
  var activeSentinel = null;
  var activeLoadingEl = null;
  var isInitialLoad = true;

  function cleanup() {
    if (activeObserver)  { activeObserver.disconnect(); activeObserver = null; }
    if (activeSentinel  && activeSentinel.parentNode)  { activeSentinel.parentNode.removeChild(activeSentinel);  activeSentinel  = null; }
    if (activeLoadingEl && activeLoadingEl.parentNode) { activeLoadingEl.parentNode.removeChild(activeLoadingEl); activeLoadingEl = null; }
  }

  function initInfiniteScroll() {
    cleanup();

    const productGrid = findGrid(document);
    if (!productGrid) return;

    lcBindProductClickSave(productGrid); // SCROLL MEMORY

    // SCROLL MEMORY: only treat this as "coming back" if the browser says
    // this load actually came from a back/forward action — never on a
    // plain refresh — and only ever on the very first init per page load.
    // Computed up front, before the pagination check below, so a
    // single-page grid (no "Next" link — small/filtered collections,
    // search results, homepage featured collections) still gets its
    // scroll position restored instead of only paginated collections.
    const scrollMemory =
      isInitialLoad && lcIsBackForwardNavigation() ? lcReadScrollMemory() : null;
    const pageSizeAtLoad = productGrid.children.length; // items already server-rendered

    // Scroll to top only on first page load, not when filters change —
    // unless we're about to restore a saved position instead.
    if (isInitialLoad) {
      if (!scrollMemory) {
        window.scrollTo(0, 0);
      }
      isInitialLoad = false;
    }

    const initialNext = findNextLink(document);
    if (!initialNext || !initialNext.href) {
      // Only one page of results — remove the class so normal pagination shows.
      document.documentElement.classList.remove('has-infinite-scroll');

      // Nothing here for us to fetch/append, so hand scroll restoration
      // back to the browser's own default behavior (undoes the 'manual'
      // mode set below in case an earlier init on this same page — e.g.
      // before a filter change trimmed the results to one page — turned
      // it on).
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'auto';
      }

      // Still honor a saved scroll position here: every product on a
      // single-page grid is already server-rendered, so no catch-up
      // fetching is needed — just reposition once layout settles.
      if (scrollMemory) {
        requestAnimationFrame(function () {
          window.scrollTo(0, scrollMemory.scrollY);
          // Late image loads can shift layout slightly — one correction pass.
          setTimeout(function () {
            window.scrollTo(0, scrollMemory.scrollY);
          }, 350);
        });
        lcClearScrollMemory();
      }
      return;
    }

    // This page has a native, paginated grid we're about to take over
    // (fetching further pages ourselves and appending them) — that's the
    // one case where the browser's own automatic scroll restoration can't
    // be trusted to land in the right place on the way back, since the
    // DOM it restores to won't yet have those appended items. Manual mode
    // is scoped to just this branch — see the file-header comment above.
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    document.documentElement.classList.add('has-infinite-scroll');

    let nextUrl = initialNext.href;
    let loading  = false;
    let done     = false;

    const loadingEl = document.createElement('div');
    loadingEl.className = 'infinite-scroll-loading';
    loadingEl.innerHTML =
      '<div class="infinite-scroll-spinner" aria-hidden="true"></div>' +
      '<span>Loading more...</span>';
    loadingEl.style.display = 'none';
    loadingEl.setAttribute('aria-live', 'polite');
    activeLoadingEl = loadingEl;

    const sentinel = document.createElement('div');
    sentinel.className = 'infinite-scroll-sentinel';
    sentinel.style.cssText = 'height: 1px; width: 100%; margin: 0; pointer-events: none;';
    sentinel.setAttribute('aria-hidden', 'true');
    activeSentinel = sentinel;

    if (productGrid.parentNode) {
      productGrid.parentNode.insertBefore(loadingEl, productGrid.nextSibling);
      productGrid.parentNode.insertBefore(sentinel,  loadingEl.nextSibling);
    }

    async function loadMore() {
      if (loading || done) return;
      loading = true;
      loadingEl.style.display = 'flex';

      try {
        const response = await fetch(nextUrl);
        if (!response.ok) throw new Error('Fetch failed: ' + response.status);

        const html = await response.text();
        const doc  = new DOMParser().parseFromString(html, 'text/html');

        appendProductsFromDoc(productGrid, doc);

        const newNext = findNextLink(doc);
        if (newNext && newNext.href && newNext.href !== nextUrl) {
          nextUrl = newNext.href;
        } else {
          done = true;
          observer.disconnect();
          sentinel.remove();
          loadingEl.remove();
        }
      } catch (err) {
        console.error('Infinite scroll error:', err);
        done = true;
        observer.disconnect();
        loadingEl.remove();
      } finally {
        loading = false;
        if (!done) loadingEl.style.display = 'none';
      }
    }

    const observer = new IntersectionObserver(
      function (entries) { if (entries[0].isIntersecting) loadMore(); },
      { rootMargin: '600px 0px' }
    );
    activeObserver = observer;
    observer.observe(sentinel);

    // SCROLL MEMORY: fresh (non-bfcache) back/forward load with a saved
    // position for this URL. Fetch every needed page IN PARALLEL (much
    // faster than one-by-one), append them in order, then scroll.
    if (scrollMemory) {
      (async function rehydrateAndRestore(target) {
        loadingEl.style.display = 'flex';
        var labelEl = loadingEl.querySelector('span');
        if (labelEl) labelEl.textContent = 'Restoring your place...';

        if (pageSizeAtLoad > 0 && target.itemCount > pageSizeAtLoad) {
          var pagesNeeded = Math.ceil(target.itemCount / pageSizeAtLoad) - 1;
          var pageUrls = [];
          for (var p = 2; p <= pagesNeeded + 1; p++) {
            pageUrls.push(buildPageUrl(nextUrl, p));
          }

          try {
            var htmlResults = await Promise.all(
              pageUrls.map(function (url) {
                return fetch(url).then(function (r) { return r.ok ? r.text() : null; });
              })
            );

            htmlResults.forEach(function (html) {
              if (!html) return;
              appendProductsFromDoc(productGrid, new DOMParser().parseFromString(html, 'text/html'));
            });

            const lastHtml = htmlResults[htmlResults.length - 1];
            if (lastHtml) {
              const lastDoc = new DOMParser().parseFromString(lastHtml, 'text/html');
              const newNext = findNextLink(lastDoc);
              if (newNext && newNext.href) {
                nextUrl = newNext.href;
              } else {
                done = true;
                observer.disconnect();
                sentinel.remove();
              }
            }
          } catch (err) {
            console.error('Scroll-memory rehydrate error:', err);
            // Falls through to the top-up loop below, which will retry
            // sequentially via the normal loadMore() path.
          }
        }

        // Top up sequentially in case the parallel batch under/over-shot
        // (e.g. inconsistent page size) or the batch above failed.
        while (!done && productGrid.children.length < target.itemCount) {
          await loadMore();
        }

        requestAnimationFrame(function () {
          window.scrollTo(0, target.scrollY);
          // Late image loads can shift layout slightly — one correction pass.
          setTimeout(function () {
            window.scrollTo(0, target.scrollY);
          }, 350);
        });
        if (loadingEl && loadingEl.parentNode && !done) loadingEl.style.display = 'none';
        lcClearScrollMemory();
      })(scrollMemory);
    }
  }

  // ---- Re-init after filter-driven navigation ----

  // Waits for the results container to actually update its DOM, then re-inits.
  // Using a MutationObserver is more reliable than a fixed timeout because
  // it fires exactly when the new content lands, regardless of network speed.
  function waitForResultsAndReinit() {
    const resultsEl = document.querySelector('[data-fragment-id="results"]');

    if (!resultsEl) {
      // Fallback: no results fragment found, just wait a beat
      setTimeout(initInfiniteScroll, 500);
      return;
    }

    const mo = new MutationObserver(function (mutations) {
      const hasChanges = mutations.some(function (m) {
        return m.addedNodes.length > 0 || m.removedNodes.length > 0;
      });
      if (!hasChanges) return;
      mo.disconnect();
      // Small extra delay lets Alpine/Splide finish their own init
      setTimeout(initInfiniteScroll, 150);
    });

    mo.observe(resultsEl, { childList: true, subtree: false });

    // Safety valve — disconnect after 8s if nothing ever changed
    setTimeout(function () { mo.disconnect(); }, 8000);
  }

  function setupFilterReinit() {
    // Intercept pushState — this is what Shopify filter JS calls when the user
    // picks a filter and the URL updates before the AJAX fetch runs.
    const origPushState = history.pushState.bind(history);
    history.pushState = function () {
      origPushState.apply(history, arguments);
      waitForResultsAndReinit();
    };

    // Back / forward buttons also change the results
    window.addEventListener('popstate', waitForResultsAndReinit);
  }

  function init() {
    initInfiniteScroll();
    // Only wire up the filter watcher on collection pages
    if (window.location.pathname.includes('/collections/')) {
      setupFilterReinit();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

// =====================================================================
// Back In Stock by region — per-variant "Notify Me" widget
//
// Replaces the old generic Klaviyo popup (KLAVIYO_FORM_ID 'TqSh6C',
// window._klOnsite.push(['openForm', ...])): that form is tied to
// Klaviyo's native product catalog, which reports ONE combined stock
// number per variant across every warehouse. A shopper in Australia
// would get told "back in stock" as soon as the US warehouse restocked,
// even if the AU warehouse — the one that actually ships to them — was
// still empty.
//
// This version calls Klaviyo's public back-in-stock-subscriptions API
// directly (via subscribeToBackInStock, imported above), targeting the
// region-specific catalog variant (bis-us-<variantId> / bis-au-<variantId>)
// populated by the "Back In Stock by region" admin page in the bundle
// app — see assets/back-in-stock-signup.js and, on the app side,
// app/klaviyo.server.js / app/routes/app.klaviyo-back-in-stock.jsx.
//
// Region is read from window.LC_WAREHOUSE_REGION, set server-side in
// layout/theme.liquid from `localization.country.iso_code` — the same
// US-vs-everyone-else rule already used by snippets/preorder-widget.liquid
// (kept identical on purpose, so "which warehouse serves this customer"
// means the same thing everywhere in this theme).
//
// Does NOT touch snippets/preorder-widget.liquid's own Klaviyo popup —
// that's a separate preorder feature and stays exactly as it was.
//
// STOQ CLEANUP: this used to bail out early whenever a Stoq/Restock
// Rocket element was on the page (`.restock-rocket-form-input`,
// `[class*=stoq]`, `[data-stoq]`), so it wouldn't fight with Stoq's own
// CTA for genuinely out-of-stock variants. Stoq's embed is off on this
// theme now, so that guard was removed — this always runs its own
// check instead.
// =====================================================================
(function () {
  const KLAVIYO_PUBLIC_API_KEY = 'TzgPWa';
  var lcOosBound = false; // replaces document.dataset.lcOosBound (document.dataset is undefined in some browsers)

  if (!window.location.pathname.includes('/products/')) return;

  // ---- Klaviyo re-injection watcher ----
  // Klaviyo's onsite embed can still auto-inject #klaviyo-bis-button-container
  // on OOS variants even though we no longer use it — keep hiding it so it
  // never shows up alongside our own widget.
  var klReinjectObserver = null;

  function startKlWatcher() {
    if (klReinjectObserver) return;
    klReinjectObserver = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var nodes = mutations[i].addedNodes;
        for (var j = 0; j < nodes.length; j++) {
          var node = nodes[j];
          if (node.nodeType !== 1) continue;
          var klEl =
            node.id === 'klaviyo-bis-button-container'
              ? node
              : node.querySelector && node.querySelector('#klaviyo-bis-button-container');
          if (klEl) klEl.style.display = 'none';
        }
      }
    });
    klReinjectObserver.observe(document.body, { childList: true, subtree: true });
  }

  function stopKlWatcher() {
    if (klReinjectObserver) {
      klReinjectObserver.disconnect();
      klReinjectObserver = null;
    }
  }

  function getATCBtn() {
    return document.querySelector('form[id^="product-form-"] .add-to-cart-container button[type="submit"]');
  }

  function getNotifyBtn() {
    return document.querySelector('.lc-notify-me-btn');
  }

  function getBisModalOverlay() {
    return document.querySelector('.lc-bis-modal-overlay');
  }

  // Reads the id → title map rendered once per product page (see
  // snippets/product-form-component-variant-picker.liquid) so the popup
  // can show exactly which variant the shopper is signing up for.
  // Cached on window since the underlying <script> tag never changes
  // after the initial page render.
  function getVariantLabel(variantId) {
    if (!window.__lcBisVariantTitles) {
      var scriptEl = document.getElementById('lc-bis-variant-titles');
      var map = {};
      if (scriptEl) {
        try { map = JSON.parse(scriptEl.textContent); } catch (e) { map = {}; }
      }
      window.__lcBisVariantTitles = map;
    }
    return window.__lcBisVariantTitles[String(variantId)] || null;
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function getVariantId() {
    // Standard (non-quick-buy) product pages render the real "id" field as
    // a <select name="id"> (see product-form-component-buy-buttons.liquid),
    // not an <input>, so we match on the name attribute alone. A <select>'s
    // .value correctly reflects its selected <option> (kept in sync by
    // Alpine via x-bind:selected) even though it sits in a display:none
    // no-js fallback wrapper.
    var field = document.querySelector('form[id^="product-form-"] [name="id"]');
    return field ? field.value : null;
  }

  // "us" or "au" — see file header. Falls back to "au" (the broader
  // bucket — everyone who isn't explicitly US) if the global somehow
  // isn't set yet, so a signup never silently fails to register at all.
  function getRegion() {
    return window.LC_WAREHOUSE_REGION === 'us' ? 'us' : 'au';
  }

  // Returns true when the selected variant is OOS. Reads
  // currentVariantAvailable directly from Alpine so we don't
  // accidentally treat the transient `loading: true` state (during
  // cart add) as OOS.
  function isCurrentVariantOOS() {
    // Primary: read from Alpine's ProductForm data
    const formIsland = document.querySelector('[x-data="ProductForm"]');
    if (formIsland && window.Alpine) {
      try {
        const data = window.Alpine.$data(formIsland);
        if (data && typeof data.currentVariantAvailable !== 'undefined') {
          if (data.loading) return false; // cart add in progress — not OOS
          return !data.currentVariantAvailable;
        }
      } catch (e) { /* fall through to button check */ }
    }

    // Fallback: check button disabled attribute (excluding loading state)
    const btn = getATCBtn();
    if (!btn) return false;
    if (btn.getAttribute('aria-busy') === 'true') return false; // loading indicator
    return btn.disabled || btn.getAttribute('aria-disabled') === 'true';
  }

  function closeBisModal() {
    var overlay = getBisModalOverlay();
    if (overlay) overlay.remove();
    document.removeEventListener('keydown', onBisModalKeydown);
  }

  function onBisModalKeydown(e) {
    if (e.key === 'Escape') closeBisModal();
  }

  // Opens a popup with an email capture form for a SPECIFIC variant.
  // Used both by the "Notify Me" button (variantId = whatever's
  // currently selected, which IS the OOS one in that case) and by
  // clicking an out-of-stock size chip directly (variantId is resolved
  // server-side — see data-bis-variant-id in
  // snippets/product-form-component-variant-picker.liquid — since
  // clicking a disabled chip never changes the picker's own selection).
  function openBisModal(variantId) {
    if (!variantId) return; // no unambiguous variant match — nothing safe to target

    var existing = getBisModalOverlay();
    if (existing) {
      if (existing.dataset.variantId === String(variantId)) {
        var existingEmailInput = existing.querySelector('.lc-bis-email-input');
        if (existingEmailInput) existingEmailInput.focus();
        return;
      }
      closeBisModal(); // a different chip was clicked — swap the target
    }

    var variantLabel = getVariantLabel(variantId);
    var regionCode = getRegion().toUpperCase(); // 'US' or 'AU'
    var subtitleHtml = variantLabel
      ? "We'll email you the moment <strong>" + escapeHtml(variantLabel) + '</strong> is back in stock.'
      : "We'll email you the moment it's back in stock.";

    var overlay = document.createElement('div');
    overlay.className = 'lc-bis-modal-overlay';
    overlay.dataset.variantId = variantId;
    overlay.innerHTML =
      '<div class="lc-bis-modal" role="dialog" aria-modal="true" aria-label="Notify me when back in stock">' +
      '<button type="button" class="lc-bis-modal-close" aria-label="Close">&times;</button>' +
      '<p class="lc-bis-modal-title">Notify when back in stock</p>' +
      '<p class="lc-bis-modal-subtitle">' + subtitleHtml + '</p>' +
      '<p class="lc-bis-modal-meta">Ships from our ' + regionCode + ' warehouse</p>' +
      '<form class="lc-bis-form">' +
      '<input type="email" required placeholder="Enter your email" class="lc-bis-email-input" autocomplete="email">' +
      '<button type="submit" class="lc-bis-submit-btn">Notify Me</button>' +
      '<div class="lc-bis-status" role="status" aria-live="polite"></div>' +
      '</form>' +
      '</div>';

    document.body.appendChild(overlay);

    var form = overlay.querySelector('.lc-bis-form');
    form.dataset.variantId = variantId;
    form.addEventListener('submit', handleBisSubmit);

    // Click on the dark backdrop (not the modal box itself) closes it.
    overlay.addEventListener('mousedown', function (e) {
      if (e.target === overlay) closeBisModal();
    });
    overlay.querySelector('.lc-bis-modal-close').addEventListener('click', closeBisModal);
    document.addEventListener('keydown', onBisModalKeydown);

    var emailInput = overlay.querySelector('.lc-bis-email-input');
    if (emailInput) emailInput.focus();
  }

  function handleBisSubmit(event) {
    event.preventDefault();
    var form = event.currentTarget;
    var emailInput = form.querySelector('.lc-bis-email-input');
    var submitBtn = form.querySelector('.lc-bis-submit-btn');
    var statusEl = form.querySelector('.lc-bis-status');
    var email = emailInput ? emailInput.value.trim() : '';
    // The popup carries its own resolved variant id (data-variant-id);
    // fall back to whatever's currently selected just in case.
    var variantId = form.dataset.variantId || getVariantId();
    var region = getRegion();

    if (!email || !variantId) return;

    submitBtn.disabled = true;
    emailInput.disabled = true;
    statusEl.textContent = 'Sending...';

    subscribeToBackInStock({
      email: email,
      variantId: variantId,
      region: region,
      klaviyoPublicApiKey: KLAVIYO_PUBLIC_API_KEY,
    })
      .then(function () {
        var modalBox = form.closest('.lc-bis-modal');
        if (modalBox) {
          modalBox.innerHTML =
            '<button type="button" class="lc-bis-modal-close" aria-label="Close">&times;</button>' +
            '<p class="lc-bis-modal-title lc-bis-success" role="status" aria-live="polite">' +
            "You're on the list — we'll email you when it's back in stock." +
            '</p>';
          modalBox.querySelector('.lc-bis-modal-close').addEventListener('click', closeBisModal);
        }
      })
      .catch(function (err) {
        console.error('[BIS] subscribe failed:', err.message);
        statusEl.textContent = 'Something went wrong — please try again.';
        submitBtn.disabled = false;
        emailInput.disabled = false;
      });
  }

  // Shows our Notify Me button and hides the disabled ATC button.
  // Also hides any Klaviyo-injected BIS element so there's no duplicate.
  //
  // Always re-applies both halves of this (hide the ATC button, AND make
  // sure exactly one Notify Me button exists) instead of skipping the
  // ATC-hide step whenever a Notify Me button already happens to be
  // there. That "skip if already done" shortcut could let the two get
  // out of sync if anything ever interrupted a previous call, leaving
  // the real button hidden with no Notify Me button in its place —
  // exactly the "no button shows at all" bug this fixes.
  function showNotifyBtn() {
    const atcBtn = getATCBtn();
    if (!atcBtn) return;

    atcBtn.style.display = 'none';

    if (!getNotifyBtn()) {
      const notifyBtn = document.createElement('button');
      notifyBtn.type = 'button';
      notifyBtn.className = 'lc-notify-me-btn';
      notifyBtn.textContent = 'Notify Me';
      notifyBtn.addEventListener('click', function () {
        openBisModal(getVariantId());
      });
      atcBtn.parentNode.insertBefore(notifyBtn, atcBtn);
    }

    // Hide Klaviyo's injected button via JS so it doesn't show alongside ours.
    // We DON'T use CSS display:none because that breaks programmatic .click().
    var klContainer = document.querySelector('#klaviyo-bis-button-container');
    if (klContainer) klContainer.style.display = 'none';

    // Watch for Klaviyo re-injecting its button after this point
    startKlWatcher();
  }

  // Restores the ATC button and closes our Notify Me widget (button AND
  // any open popup).
  function hideNotifyBtn() {
    const notifyBtn = getNotifyBtn();
    if (notifyBtn) notifyBtn.remove();
    closeBisModal();

    const atcBtn = getATCBtn();
    if (atcBtn) atcBtn.style.display = '';

    // Restore Klaviyo's container so it can be clicked normally
    var klContainer = document.querySelector('#klaviyo-bis-button-container');
    if (klContainer) klContainer.style.removeProperty('display');

    // Stop watching for Klaviyo re-injections
    stopKlWatcher();
  }

  // Applies the correct button (Add to Cart vs Notify Me) for whatever
  // the variant state is AT THE MOMENT this runs.
  function applyNotifyState() {
    if (isCurrentVariantOOS()) {
      showNotifyBtn();
    } else {
      hideNotifyBtn();
    }
  }

  // Safety net: something else on this page can, on rare occasions,
  // reset the Add to Cart button's visibility shortly after a variant
  // switch (seen intermittently in testing, cause not fully isolated —
  // happens even though our own code applies the right state immediately).
  // Rather than leave the button broken until the next switch, re-apply
  // the correct state a few more times over the following second. Each
  // check reads the LIVE variant state when it runs, so if the shopper
  // switches again in the meantime these just confirm the newer,
  // already-correct state instead of fighting it.
  function syncNotifyButton() {
    applyNotifyState();
    [150, 400, 800].forEach(function (delay) {
      setTimeout(applyNotifyState, delay);
    });
  }

  // Uses event delegation on the document so it works regardless of when
  // the OOS class is applied (resizable-option.js adds .out-of-stock after
  // 1 s, which is after our 500 ms init — direct binding would miss them).
  function setupOOSChipListeners() {
    if (lcOosBound) return;
    lcOosBound = true;

    document.addEventListener('click', function (e) {
      var el = e.target;
      var oosChip =
        el.closest('.label-only-input[data-unavailable="true"]') ||
        el.closest('.resizable-option.out-of-stock') ||
        el.closest('.bundle-label[data-unavailable="true"]') ||
        el.closest('.bundle-resizable-span[data-unavailable="true"]');

      if (!oosChip) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      // data-bis-variant-id is rendered server-side (see
      // snippets/product-form-component-variant-picker.liquid) on the
      // standard size/option chips — resolves to the exact variant that
      // combination represents. Not yet present on resizable-option or
      // bundle-picker chips (a separate, more complex mapping), so those
      // paths still no-op for now rather than guessing wrong.
      var chipWithId = oosChip.hasAttribute('data-bis-variant-id') ? oosChip : oosChip.closest('[data-bis-variant-id]');
      var variantId = chipWithId ? chipWithId.getAttribute('data-bis-variant-id') : null;
      openBisModal(variantId);
    }, true); // capture phase — runs before picker JS handlers
  }

  function init() {
    // OOS chip listeners run immediately — they don't need the ATC container
    setupOOSChipListeners();

    // The inline Notify Me button requires the ATC container — wait for Alpine
    setTimeout(function () {
      const atcContainer = document.querySelector('form[id^="product-form-"] .add-to-cart-container');
      if (!atcContainer) return;

      // Single source of truth for "did the variant change": the
      // theme:variant:change event. A MutationObserver watching the
      // button's disabled/aria-disabled attributes used to run alongside
      // this and fire independently for the same logical change (Alpine
      // updates currentVariant, which both dispatches this event AND
      // flips the button's disabled attribute) — the two triggers could
      // race and run syncNotifyButton() twice with inconsistent
      // in-between state, which is what caused the Notify Me / Add to
      // Cart buttons to flash both-visible or both-hidden for an instant
      // on a variant switch. Removed rather than reconciled: the loading
      // guard inside isCurrentVariantOOS() already covers the one case
      // (cart-add in progress) where disabled could change for a reason
      // other than a variant switch, so this event alone is sufficient.
      document.addEventListener('theme:variant:change', syncNotifyButton);

      syncNotifyButton(); // check initial state
    }, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

// =====================================================================
// Scroll to top button
//
// Threshold (in viewport heights) comes from the button's
// data-scroll-threshold attribute, which snippets/scroll-to-top.liquid
// sets from Theme settings (enable_scroll_to_top /
// scroll_to_top_threshold_standard / scroll_to_top_threshold_pdp).
//
// Dispatches two events other scripts/analytics can listen for:
//   - "lc:scroll-to-top-shown"   fired once per page load, the first
//                                 time the button becomes visible
//   - "lc:scroll-to-top-clicked" fired every time it's clicked
// =====================================================================
(function () {
  function init() {
    var btn = document.getElementById('lc-scroll-to-top');
    if (!btn || btn.dataset.lcSttBound === 'true') return;
    btn.dataset.lcSttBound = 'true';

    var thresholdMultiplier = parseFloat(btn.dataset.scrollThreshold) || 2;
    var ticking = false;
    var hasFiredShownEvent = false;

    function thresholdPx() {
      // Recalculated live so resizing/rotating keeps it accurate.
      return window.innerHeight * thresholdMultiplier;
    }

    function update() {
      var shouldShow = window.scrollY > thresholdPx();

      if (shouldShow !== btn.classList.contains('is-visible')) {
        btn.classList.toggle('is-visible', shouldShow);
        btn.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
        btn.tabIndex = shouldShow ? 0 : -1;

        if (shouldShow && !hasFiredShownEvent) {
          hasFiredShownEvent = true;
          document.dispatchEvent(new CustomEvent('lc:scroll-to-top-shown'));
        }
      }

      ticking = false;
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    btn.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      document.dispatchEvent(new CustomEvent('lc:scroll-to-top-clicked'));
    });

    update();
    initChatWidgetAlignment(btn);
  }

  // ---------------------------------------------------------------------
  // Align vertically with the chat widget bubble (Gorgias — confirmed
  // via its iframe title on this store). Reads its actual on-screen
  // bottom offset so the two buttons sit at the same height, no matter
  // the breakpoint. Falls back to the CSS default (in scroll-to-top.css)
  // if no known chat widget is found.
  // ---------------------------------------------------------------------
  var CHAT_WIDGET_SELECTORS = [
    'iframe[title="Gorgias live chat messenger"]', // confirmed selector for this store
    '#gorgias-chat-container',
    '#gorgias-chat-widget',
    '.gorgias-chat-container',
    '#chat-widget-container', // Zendesk
    '#launcher', // Zendesk classic
    '.intercom-launcher',
    '#tidio-chat-iframe',
  ];

  // Manual fine-tune on top of the detected position. The Gorgias launcher
  // iframe has some invisible padding around the visible bubble, so the
  // raw bounding box isn't a perfect match — this nudges the button up
  // to compensate. Adjust this number if it drifts after a Gorgias update.
  var CHAT_WIDGET_ALIGNMENT_CORRECTION_PX = 10;

  function initChatWidgetAlignment(btn) {
    var attempts = 0;

    function findChatWidget() {
      for (var i = 0; i < CHAT_WIDGET_SELECTORS.length; i++) {
        var el = document.querySelector(CHAT_WIDGET_SELECTORS[i]);
        if (el && el.getBoundingClientRect().height > 0) return el;
      }
      return null;
    }

    function align() {
      var chatEl = findChatWidget();
      if (!chatEl) {
        attempts += 1;
        if (attempts < 6) setTimeout(align, 800); // widget likely still loading async
        return;
      }
      var rect = chatEl.getBoundingClientRect();
      var bottomOffset = (window.innerHeight - rect.bottom) + CHAT_WIDGET_ALIGNMENT_CORRECTION_PX;
      if (bottomOffset >= 0 && bottomOffset < 200) {
        btn.style.bottom = 'calc(' + Math.round(bottomOffset) + 'px + env(safe-area-inset-bottom, 0px))';
      }
    }

    align();
    window.addEventListener('resize', align);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-bind if the section/layout re-renders in the theme editor
  document.addEventListener('shopify:section:load', init);
})();


// find your size
//
// Clicking any "find your size" link (footer, About menu, size guide
// drawer, etc.):
//   - If the Aitaca widget is already on the current page (ring PDP),
//     opens it directly, no navigation.
//   - Otherwise redirects to a ring product page that has the widget
//     (Till Death Ring | Silver, handle "till-death" — permanent
//     best-seller) and opens the widget there automatically once it
//     renders. This is done via the "open-size-finder=1" query param,
//     which checkAutoOpenParam() picks up on the next page load.
(function () {
  var REDIRECT_PRODUCT_URL = '/products/till-death';

  function mergeSizeRow() {
    var aitaca = document.querySelector('.aitaca-widget-container');
    if (!aitaca || aitaca.dataset.merged) return;

    var prompt = document.createElement('span');
    prompt.className = 'lc-size-prompt';
    prompt.textContent = "Not sure?";

    var wrapper = document.createElement('div');
    wrapper.style.cssText = 'display:flex;align-items:center;gap:6px;flex-wrap:wrap;margin:12px 0 0;';
    aitaca.style.margin = '0';

    aitaca.parentNode.insertBefore(wrapper, aitaca);
    wrapper.appendChild(prompt);
    wrapper.appendChild(aitaca);
    aitaca.dataset.merged = 'true';
  }

  document.addEventListener('DOMContentLoaded', mergeSizeRow);
  var mergeObserver = new MutationObserver(mergeSizeRow);
  mergeObserver.observe(document.body, { childList: true, subtree: true });
  setTimeout(function () { mergeObserver.disconnect(); }, 5000);

  function closeDrawerIfOpen() {
    try {
      if (window.Alpine && typeof Alpine.store === 'function') {
        var modalsStore = Alpine.store('modals');
        if (modalsStore && typeof modalsStore.close === 'function') {
          modalsStore.close('rightDrawer');
        }
      }
    } catch (err) {
      console.warn('No se pudo cerrar el drawer:', err);
    }
  }

  // Polls for .aitaca-button (it can take a moment to render after a
  // fresh navigation) and clicks it as soon as it shows up.
  function waitAndOpenAitaca(maxWaitMs) {
    var start = Date.now();
    var interval = setInterval(function () {
      var btn = document.querySelector('.aitaca-button');
      if (btn) {
        clearInterval(interval);
        btn.click();
      } else if (Date.now() - start > maxWaitMs) {
        clearInterval(interval);
        console.warn('No se encontró .aitaca-button luego de redirigir a ' + REDIRECT_PRODUCT_URL);
      }
    }, 150);
  }

  // If we just landed here via a "find your size" redirect, open the
  // widget automatically once it renders, then strip the param from
  // the URL so a refresh/back doesn't re-trigger it.
  function checkAutoOpenParam() {
    var params = new URLSearchParams(window.location.search);
    if (params.get('open-size-finder') !== '1') return;

    waitAndOpenAitaca(8000);

    params.delete('open-size-finder');
    var newSearch = params.toString();
    var newUrl = window.location.pathname + (newSearch ? '?' + newSearch : '') + window.location.hash;
    history.replaceState(null, '', newUrl);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', checkAutoOpenParam);
  } else {
    checkAutoOpenParam();
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('a[href*="findyourringsize"]');
    if (!trigger) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    var aitacaButton = document.querySelector('.aitaca-button');

    if (aitacaButton) {
      closeDrawerIfOpen();
      setTimeout(function () {
        aitacaButton.click();
      }, 50);
      return;
    }

    closeDrawerIfOpen();
    window.location.href = REDIRECT_PRODUCT_URL + '?open-size-finder=1';
  }, true);
})();